export type Parameter = {
    readonly name: string;
    readonly type: string;
    readonly required?: boolean;
    readonly description: string;
};

export type Endpoint = {
    readonly id: string;
    readonly title: string;
    readonly method: 'GET' | 'POST';
    readonly path: string;
    readonly description: string;
    readonly parameters?: readonly Parameter[];
    readonly request?: string;
    readonly response?: string;
    readonly responseExamples?: readonly {
        readonly title: string;
        readonly content: string;
    }[];
    readonly note?: string;
};

export type PaymentMethodInfo = {
    readonly name: string;
    readonly tag: string;
    readonly countries: readonly string[];
    readonly currencies: readonly string[];
};

export const paymentMethodCatalog: readonly PaymentMethodInfo[] = [
    { name: 'Bancontact', tag: 'bancontact', countries: ['BE'], currencies: ['EUR'] },
    { name: 'Bizum', tag: 'bizum', countries: ['ES'], currencies: ['EUR'] },
    { name: 'BLIK', tag: 'blik', countries: ['PL'], currencies: ['PLN'] },
    {
        name: 'Cards',
        tag: 'card',
        countries: ['AT', 'BE', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'IT', 'NL', 'NO', 'PL', 'PT', 'RO', 'ES', 'SE', 'CH', 'GB'],
        currencies: ['EUR', 'GBP', 'AUD', 'DKK', 'NOK', 'PLN', 'SEK', 'CHF']
    },
    { name: 'EPS', tag: 'eps', countries: ['AT'], currencies: ['EUR'] },
    { name: 'Giropay', tag: 'giropay', countries: ['DE'], currencies: ['EUR'] },
    { name: 'iDEAL', tag: 'ideal', countries: ['NL'], currencies: ['EUR'] },
    { name: 'Klarna Pay Now', tag: 'klarnapaynow', countries: ['DE'], currencies: ['EUR'] },
    { name: 'Multibanco', tag: 'multibanco', countries: ['PT'], currencies: ['EUR'] },
    { name: 'MBWay', tag: 'mbway', countries: ['PT'], currencies: ['EUR'] },
    { name: 'MobilePay', tag: 'mobilepay', countries: ['DK', 'FI'], currencies: ['EUR'] },
    {
        name: 'Neteller',
        tag: 'neteller',
        countries: ['AT', 'BE', 'HR', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IE', 'IT', 'LV', 'LI', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'],
        currencies: ['EUR', 'DKK', 'GBP', 'NOK', 'PLN', 'SEK', 'RON']
    },
    {
        name: 'Open Banking',
        tag: 'ob',
        countries: ['SE', 'FI', 'NL', 'DE', 'GB'],
        currencies: ['SEK (SE)', 'EUR (FI, NL, DE)', 'GBP (GB)']
    },
    { name: 'PayID', tag: 'payid', countries: ['AU'], currencies: ['AUD'] },
    { name: 'Paysafecard', tag: 'paysafecard', countries: ['DE', 'FR', 'ES', 'IT', 'AT', 'NL', 'PL', 'SE', 'NO', 'DK', 'BE', 'CZ', 'PT', 'IE'], currencies: ['EUR'] },
    { name: 'Paysafecash', tag: 'paysafecash', countries: ['DE', 'FR', 'ES', 'IT', 'AT', 'NL', 'PL', 'SE', 'NO', 'DK', 'BE', 'CZ', 'PT', 'IE'], currencies: ['EUR'] },
    { name: 'PIX', tag: 'pix', countries: ['BR'], currencies: ['BRL'] },
    { name: 'Przelewy24', tag: 'p24', countries: ['PL'], currencies: ['PLN', 'EUR'] },
    { name: 'RapidTransfer', tag: 'rapidtransfer', countries: ['DE', 'FR', 'ES', 'IT', 'AT', 'NL', 'PL', 'SE', 'NO', 'DK', 'BE', 'CZ', 'PT', 'IE'], currencies: ['EUR'] },
    { name: 'Safetypay', tag: 'safetypay', countries: ['AT', 'BE', 'BR', 'CL', 'DE', 'EC', 'MX', 'NL', 'PE', 'ES'], currencies: ['USD', 'EUR', 'MXN'] },
    { name: 'Sepa Transfer', tag: 'sepatransfer', countries: ['AT', 'BE', 'FR', 'IT', 'LT', 'LU', 'MT'], currencies: ['EUR'] },
    {
        name: 'Skrill',
        tag: 'skrill',
        countries: ['AT', 'BE', 'HR', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IE', 'IT', 'LV', 'LI', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'],
        currencies: ['EUR', 'DKK', 'GBP', 'NOK', 'PLN', 'SEK', 'RON']
    },
    { name: 'SPEI', tag: 'spei', countries: ['MX'], currencies: ['MXN'] },
    { name: 'Swish', tag: 'swish', countries: ['SE'], currencies: ['SEK'] },
    { name: 'Trustly', tag: 'trustly', countries: ['AT', 'DK', 'EE', 'FI', 'DE', 'LV', 'LT', 'NL', 'NO', 'PL', 'ES', 'SE', 'GB'], currencies: ['EUR', 'DKK', 'GBP', 'NOK', 'PLN', 'SEK'] },
    { name: 'Vipps', tag: 'vipps', countries: ['NO'], currencies: ['NOK'] },
    { name: 'Wero', tag: 'wero', countries: ['DE'], currencies: ['EUR'] }
];

export const transactionParameters: readonly Parameter[] = [
    { name: 'api_id', type: 'Long', required: true, description: 'Merchant API identifier provided by Macropay.' },
    { name: 'api_guid', type: 'String', required: true, description: 'Merchant API secret provided by Macropay.' },
    { name: 'channel', type: 'String', required: true, description: 'Use testchannel for testing and livechannel for production.' },
    { name: 'tag', type: 'String', required: true, description: 'Exact payment method tag returned by the payment-methods endpoint.' },
    { name: 'currency', type: 'String', required: true, description: 'ISO 4217 transaction currency.' },
    { name: 'amount', type: 'Decimal', required: true, description: 'Transaction amount, using a dot and no more than two decimals.' },
    { name: 'countrycode', type: 'String', required: true, description: 'ISO country code for the country where the transaction takes place.' },
    { name: 'trackingmembercode', type: 'String', required: true, description: 'Merchant order or tracking reference. It must be unique for 24 hours.' },
    { name: 'transaction_count', type: 'Integer', required: false, description: 'Player transaction count known by the merchant at the time of the request.' },
    { name: 'merchantredirecturl', type: 'URL', required: true, description: 'Destination after the consumer completes or abandons the payment.' },
    { name: 'notificationurl', type: 'URL', required: true, description: 'Endpoint that receives transaction status notifications.' },
    { name: 'source_url', type: 'URL', required: true, description: 'Exact originating merchant website.' },
    { name: 'source_ip', type: 'IP address', required: true, description: 'Actual IP address of the end user.' },
    { name: 'customer', type: 'Object', required: true, description: 'JSON object containing the customer fields listed below. Do not send these fields as dot-separated top-level properties.' },
    { name: 'customer.first_name', type: 'String', required: true, description: 'Customer first name, between 3 and 32 characters.' },
    { name: 'customer.last_name', type: 'String', required: true, description: 'Customer last name, between 3 and 64 characters.' },
    { name: 'customer.email', type: 'Email', required: true, description: 'Valid customer email address.' },
    { name: 'customer.phone', type: 'String', required: true, description: 'Customer phone including country code; 5 to 20 characters, without special characters.' },
    { name: 'customer.phone_verified', type: 'Integer', required: false, description: 'Whether the phone is verified. Allowed values: 0 or 1.' },
    { name: 'customer.address', type: 'String', required: false, description: 'Customer address, up to 128 characters, without special characters.' },
    { name: 'customer.zip', type: 'String', required: false, description: 'Customer postal code, up to 20 characters, without special characters.' },
    { name: 'customer.city', type: 'String', required: false, description: 'Customer city, up to 128 characters, without special characters.' },
    { name: 'customer.country', type: 'String', required: false, description: 'ISO 3166 numeric country code.' },
    { name: 'specin.*', type: 'Varies', required: false, description: 'Payment-method-specific top-level fields such as specin.bic. Unlike customer, these keys are dot-separated field names.' },
];

export const processorEndpoints: readonly Endpoint[] = [
    {
        id: 'create-transaction',
        title: 'Create a transaction',
        method: 'POST',
        path: '/v1/transaction',
        description: 'Create a payment and receive the URL used to redirect the consumer to the selected payment method.',
        parameters: transactionParameters,
        request: `{
  "api_id": 117195987,
  "api_guid": "YOUR-API-GUID",
  "channel": "testchannel",
  "tag": "giropay",
  "source_ip": "172.217.17.110",
  "source_url": "https://www.merchantsite.com",
  "currency": "EUR",
  "amount": 15.00,
  "countrycode": "DE",
  "trackingmembercode": "order-010",
  "transaction_count": 12,
  "merchantredirecturl": "https://merchant.com/payment-result",
  "notificationurl": "https://merchant.com/notifications",
  "customer": {
    "first_name": "John",
    "last_name": "Doe",
    "email": "john.doe@example.com",
    "phone": "+49123456789"
  }
}`,
        response: `{
  "ResultState": true,
  "ResultCode": 0,
  "ResultMessage": "PENDING",
  "ResultTransactionId": "1824169550",
  "ResultTransactionGuid": "80290ef1-ee35-4186-93dd-e222fc503eba",
  "ResultCdcData": {
    "REDIRECTURL": "https://checkout.example/continue/...",
    "TAG": "giropay",
    "STATUS": "PENDING",
    "CHANNEL": "testchannel"
  }
}`,
        note: 'Required: include source_url with the exact originating website for every transaction request. The redirect URL returned by Macropay is single-use; do not validate it with a GET request before sending the consumer to it.'
    },
    {
        id: 'transaction-status',
        title: 'Get transaction status',
        method: 'POST',
        path: '/v1/getTransactionStatus',
        description: 'Retrieve the latest transaction, funds and settlement status.',
        parameters: [
            { name: 'api_id', type: 'Long', required: true, description: 'Merchant API identifier.' },
            { name: 'api_guid', type: 'String', required: true, description: 'Merchant API secret.' },
            { name: 'transaction_id', type: 'String', required: true, description: 'Transaction identifier returned when the payment was created.' },
            { name: 'transaction_guid', type: 'UUID', required: true, description: 'Transaction GUID returned when the payment was created.' }
        ],
        request: `{
  "api_id": 117195987,
  "api_guid": "YOUR-API-GUID",
  "transaction_id": "2483750937",
  "transaction_guid": "ce7ad01d-6842-4788-9e97-168b4dc2056f"
}`,
        responseExamples: [
            {
                title: 'Response without decision',
                content: `{
  "ResultState": true,
  "ResultCode": 0,
  "ResultCdcData": {
    "transaction_id": "2483750937",
    "transaction_guid": "ce7ad01d-6842-4788-9e97-168b4dc2056f",
    "tracking_member_code": "order-010",
    "amount": "10.000",
    "currency": "EUR",
    "tag": "ideal",
    "channel": "testchannel",
    "status": "PENDING",
    "fundsstatus": null,
    "settlement_fundsstatus": null,
    "customer": {
      "first_name": "John",
      "last_name": "Doe",
      "email": "john.doe@example.com"
    },
    "partials": []
  },
  "ResultDecision": []
}`
            },
            {
                title: 'Response with decision',
                content: `{
  "ResultState": true,
  "ResultCode": 0,
  "ResultCdcData": {
    "transaction_id": "2396543143",
    "transaction_guid": "2ba4d857-b4e9-48de-90c2-62b88c732593",
    "tracking_member_code": "order-011",
    "amount": "10.000",
    "currency": "EUR",
    "tag": "ideal",
    "channel": "testchannel",
    "status": "SUCCEEDED",
    "fundsstatus": "WAITING",
    "settlement_fundsstatus": null,
    "customer": {
      "first_name": "John",
      "last_name": "Doe",
      "email": "john.doe@example.com"
    },
    "partials": []
  },
  "ResultDecision": {
    "ResultCode": 0,
    "ResultState": true,
    "ResultMessage": "SUCCEEDED",
    "ResultTransactionId": "2396543143",
    "ResultTransactionGuid": "2ba4d857-b4e9-48de-90c2-62b88c732593",
    "ResultTrackingMemberCode": "order-011",
    "ResultCdcData": {
      "TXID": "2396543143",
      "FLAGS": "C",
      "ERRMSG": "",
      "STATE_NAME": "PAID"
    }
  }
}`
            }
        ]
    },
    {
        id: 'payment-methods',
        title: 'List available payment methods',
        method: 'GET',
        path: '/v1/payment-methods',
        description: 'Returns the payment methods enabled for the merchant together with their countries, currencies and method-specific fields.',
        responseExamples: [
            {
                title: 'Payment method without specific fields',
                content: `{
  "tag": "ideal",
  "countriesAvailable": "NL",
  "currenciesAvailable": "EUR,PLN",
  "requireSpecIn": 0,
  "specInParameters": []
}`
            },
            {
                title: 'Payment method with a specific field',
                content: `{
  "tag": "example",
  "countriesAvailable": "DE",
  "currenciesAvailable": "EUR",
  "requireSpecIn": 1,
  "specInParameters": [
    {
      "methodSpecInField": "specin.bic",
      "methodSpecInFieldIsMandatory": 1,
      "methodSpecInFieldType": "text",
      "methodSpecInFieldDescription": "BIC of the consumer's bank",
      "methodSpecInFieldOptions": null
    }
  ]
}`
            }
        ],
        note: 'The live list for the signed-in merchant is available in the Payment methods section below.'
    },
    {
        id: 'get-consumer',
        title: 'Get consumer',
        method: 'POST',
        path: '/v1/check-consumer',
        description: 'Retrieve whitelist, blacklist and KYC information for a consumer email address.',
        parameters: [
            { name: 'email', type: 'Email', required: true, description: 'Consumer email address.' }
        ],
        request: `{
  "email": "consumer@example.com"
}`,
        response: `{
  "email": "consumer@example.com",
  "hash": "hashed-email",
  "isWhitelisted": true,
  "isBlacklisted": false,
  "hasKyc": true
}`
    },
    {
        id: 'consumer-kyc',
        title: 'Consumer whitelist and KYC',
        method: 'POST',
        path: '/v1/consumer-kycs',
        description: 'Whitelist a consumer and securely upload the supporting KYC document.',
        parameters: [
            { name: 'file', type: 'File', required: true, description: 'PDF, JPG, JPEG, PNG, RAR or ZIP, up to 50 MB.' },
            { name: 'verified_first_name', type: 'String', required: true, description: 'Verified first name.' },
            { name: 'verified_last_name', type: 'String', required: true, description: 'Verified last name.' },
            { name: 'verified_dob', type: 'Date', required: true, description: 'YYYY-MM-DD; consumer must be at least 18.' },
            { name: 'address', type: 'String', required: true, description: 'Verified address, up to 128 characters.' },
            { name: 'verified_nationality', type: 'String', required: true, description: 'Uppercase ISO alpha-2 nationality.' },
            { name: 'phone', type: 'String', required: true, description: 'Verified phone number.' },
            { name: 'email', type: 'Email', required: true, description: 'Consumer email address.' }
        ]
    }
];

export const whitelistEndpoints: readonly Endpoint[] = [
    {
        id: 'whitelist-list',
        title: 'List whitelist imports',
        method: 'POST',
        path: '/merchant/api/emails-whitelist/l/i/s/t',
        description: 'List and filter the merchant whitelist import history.',
        parameters: [
            { name: 'fileKey', type: 'String', required: false, description: 'Filter imports by file key. Send as fileKey=value in the request body.' },
            { name: 'coreId[in][]', type: 'Integer[]', required: false, description: 'Filter by one or more core IDs. Repeat coreId[in][]=value in the request body.' }
        ]
    },
    { id: 'whitelist-template', title: 'Download whitelist template', method: 'GET', path: '/merchant/api/template-emails-whitelist', description: 'Download the EDD whitelist CSV template. The transaction_count column is optional.' },
    { id: 'whitelist-template-no-sdd', title: 'Download simplified template', method: 'GET', path: '/merchant/api/template-emails-whitelist-without-sdd', description: 'Download the simplified whitelist CSV template without SDD fields. The transaction_count column is optional.' },
    {
        id: 'whitelist-import',
        title: 'Import whitelist',
        method: 'POST',
        path: '/merchant/api/emails-whitelist',
        description: 'Upload a completed whitelist CSV file.',
        parameters: [{ name: 'file', type: 'File', required: true, description: 'Completed whitelist CSV file sent as multipart/form-data.' }]
    }
];

const coreIdParameter: readonly Parameter[] = [
    { name: 'coreId', type: 'Integer', required: true, description: 'Resource identifier inserted in the URL path.' }
];

const filePathParameters: readonly Parameter[] = [
    ...coreIdParameter,
    { name: 'key', type: 'String', required: true, description: 'File key returned by the corresponding payout list endpoint.' }
];

export const directApiEndpoints: readonly Endpoint[] = [
    { id: 'direct-transactions', title: 'Transactions', method: 'POST', path: '/merchant/api/transactions/l/i/s/t', description: 'List, filter, sort and export merchant transactions.' },
    { id: 'direct-payout-files', title: 'Payout files', method: 'POST', path: '/merchant/api/merchant-payout-files/l/i/s/t', description: 'List merchant payout files.' },
    { id: 'direct-payout-summary', title: 'Payout summary files', method: 'POST', path: '/merchant/api/merchant-payout-summary-files/l/i/s/t', description: 'List merchant payout summary files.' },
    { id: 'direct-bank-accounts', title: 'Payout accounts', method: 'POST', path: '/merchant/api/payout-accounts/l/i/s/t', description: 'List the merchant payout accounts.' },
    { id: 'direct-crypto-accounts', title: 'Payout crypto accounts', method: 'POST', path: '/merchant/api/payout-crypto-accounts/l/i/s/t', description: 'List the merchant payout crypto accounts.' },
    { id: 'direct-errors', title: 'API error message codes', method: 'POST', path: '/merchant/api/api-error-messages-codes/l/i/s/t', description: 'Retrieve API error codes. Cache the response and refresh it at most weekly.' },
    { id: 'direct-history', title: 'Transaction history', method: 'POST', path: '/merchant/api/transactions/{coreId}/histories/l/i/s/t', description: 'Retrieve state changes for a transaction.', parameters: coreIdParameter },
    { id: 'direct-notifications', title: 'Notification history', method: 'POST', path: '/merchant/api/transactions/{coreId}/notifications-out/l/i/s/t', description: 'Retrieve outgoing notification attempts for a transaction.', parameters: coreIdParameter },
    { id: 'direct-import-whitelist', title: 'Import emails whitelist', method: 'POST', path: '/merchant/api/emails-whitelist', description: 'Import consumer whitelist records from a CSV file.', parameters: [{ name: 'file', type: 'File', required: true, description: 'Completed whitelist CSV file sent as multipart/form-data.' }] },
    { id: 'direct-emails-whitelist', title: 'Emails whitelist', method: 'POST', path: '/merchant/api/emails-whitelist/l/i/s/t', description: 'List whitelist import records.' },
    { id: 'direct-download-payout', title: 'Download payout file', method: 'GET', path: '/merchant/api/merchant-payout-files/{coreId}/files/{key}', description: 'Download a payout XLS file.', parameters: filePathParameters },
    { id: 'direct-download-summary', title: 'Download payout summary file', method: 'GET', path: '/merchant/api/merchant-payout-summary-files/{coreId}/files/{key}', description: 'Download a payout summary XLS file.', parameters: filePathParameters },
    { id: 'direct-summary-transactions', title: 'Payout summary transactions CSV', method: 'GET', path: '/merchant/api/merchant-payout-summary-file-csv-transactions/{coreId}', description: 'Download payout summary transactions as CSV.', parameters: coreIdParameter },
    { id: 'direct-summary-payouts', title: 'Payout summary consumer payouts CSV', method: 'GET', path: '/merchant/api/merchant-payout-summary-file-csv-consumer-payouts/{coreId}', description: 'Download payout summary consumer payouts as CSV.', parameters: coreIdParameter },
    { id: 'direct-file-transactions', title: 'Payout file transactions CSV', method: 'GET', path: '/merchant/api/merchant-payout-file-csv-transactions/{coreId}', description: 'Download payout file transactions as CSV.', parameters: coreIdParameter },
    { id: 'direct-file-summary', title: 'Payout file pending settlement transactions CSV', method: 'GET', path: '/merchant/api/merchant-payout-file-csv-pending-settlement-transactions/{coreId}', description: 'Download payout file pending settlement transactions as CSV.', parameters: coreIdParameter },
    { id: 'direct-file-payouts', title: 'Payout file consumer payouts CSV', method: 'GET', path: '/merchant/api/merchant-payout-file-csv-consumer-payouts/{coreId}', description: 'Download payout file consumer payouts as CSV.', parameters: coreIdParameter }
];

export const errorMessages = [
    ['User is not in ecosystem', 'The user email has not been whitelisted. The transaction will be rejected.'],
    ['Minimum ticket size mismatch. Minimum ticket size is 10.00', 'The minimum transaction amount is 10 EUR or equivalent.'],
    ['Maximum ticket size mismatch. Maximum ticket size is 1,000.00 EUR or equivalent', 'The maximum transaction amount is 1,000 EUR or equivalent.'],
    ['Test transaction is not allowed on LIVE environment', 'A testchannel request was sent to the livechannel endpoint.'],
    ['Exceeded maximum number of transaction within 24hrs with same profile. Quota is 5', 'The default quota is five succeeded transactions per user, payment method and 24 hours. This may differ based on the commercial agreement.'],
    ['Exceeded maximum total amount with the same profile in 24h. Quota is 5,000.00 EUR', 'The default quota is 5,000 EUR per user, payment method and 24 hours. This may differ based on the commercial agreement.'],
    ['Tracking member code not set or must be unique for 24hs', 'The tracking member code must be provided and unique for 24 hours.'],
    ['This method is not in use for this account', 'The requested payment method is not enabled for the merchant account. Contact support.'],
    ['Transaction pace too high', 'The default velocity limit prevents consecutive succeeded transactions for the same user within 180 seconds. It applies to pending or succeeded transactions and may differ based on the commercial agreement.'],
    ['High risk consumer profile', 'The consumer has been blacklisted. Contact the compliance team for further details.']
] as const;
